import { settle, toMs } from "@scripts/settle";

const CONTROL = "[data-fa-filter-tag]";
const VALUE = "data-fa-filter-tag-value";
const PRESSED = "aria-pressed";
const HIDDEN = "hidden";
const INERT = "inert";

export type FilterListOptions = {
	root: HTMLElement;
	filters: HTMLElement;
	items: readonly HTMLElement[];
	allValue: string;
	categoryAttribute: string;
	/** The block's own BEM class. Every state class and custom property derives from it. */
	block: string;
	inertTarget: HTMLElement;
	applyVisible: (matched: readonly HTMLElement[]) => readonly HTMLElement[];
	onFilterChange?: (value: string) => void;
	onApplied?: (matched: readonly HTMLElement[], shown: readonly HTMLElement[]) => void;
	onReflow?: () => void;
};

export type FilterList = {
	refresh: (animate?: boolean) => void;
	restore: () => void;
};

export const fillTemplate = (template: string, values: Record<string, number | string>): string =>
	Object.entries(values).reduce(
		(text, [key, value]) => text.replaceAll(`{${key}}`, String(value)),
		template,
	);

export const initFilterList = (options: FilterListOptions, signal: AbortSignal): FilterList => {
	const {
		root,
		filters,
		items,
		allValue,
		categoryAttribute,
		block,
		inertTarget,
		applyVisible,
		onFilterChange,
		onApplied,
		onReflow,
	} = options;

	const exitingClass = `${block}--exiting`;
	const emptyClass = `${block}--empty`;
	const enteringClass = `${block}__item--entering`;
	const indexProperty = `--${block}-enter-index`;
	const switchProperty = `--${block}-switch`;

	const controls = [...filters.querySelectorAll<HTMLElement>(CONTROL)];
	const authored = {
		pressed: new Map(controls.map((control) => [control, control.getAttribute(PRESSED)])),
		hidden: new Map(items.map((item) => [item, item.hasAttribute(HIDDEN)])),
	};

	const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

	let cancelHold: (() => void) | undefined;
	let frame = 0;
	let active = allValue;

	const matches = (item: HTMLElement, value: string) =>
		value === allValue ||
		item.querySelector(`[${categoryAttribute}]`)?.getAttribute(categoryAttribute) === value;

	const clearEntering = () => {
		for (const item of items) item.classList.remove(enteringClass);
	};

	const applyFilter = (animate: boolean) => {
		const matched = items.filter((item) => matches(item, active));
		const shown = applyVisible(matched);

		for (const item of items) {
			const entering = animate && shown.includes(item);

			item.classList.toggle(enteringClass, entering);

			if (entering) item.style.setProperty(indexProperty, String(shown.indexOf(item)));
			else item.style.removeProperty(indexProperty);
		}

		root.classList.toggle(emptyClass, matched.length === 0);
		root.classList.remove(exitingClass);
		inertTarget.removeAttribute(INERT);

		onReflow?.();
		onApplied?.(matched, shown);

		frame = requestAnimationFrame(() => {
			onReflow?.();
			frame = requestAnimationFrame(clearEntering);
		});
	};

	const run = (animate: boolean) => {
		cancelHold?.();
		cancelHold = undefined;
		cancelAnimationFrame(frame);
		clearEntering();

		if (!animate || reduced.matches) {
			applyFilter(false);
			return;
		}

		root.classList.add(exitingClass);
		inertTarget.setAttribute(INERT, "");

		cancelHold = settle(
			root,
			toMs(getComputedStyle(root).getPropertyValue(switchProperty)),
			() => {
				cancelHold = undefined;
				applyFilter(true);
			},
		);
	};

	const press = (value: string) => {
		const selected = value === active ? allValue : value;

		for (const control of controls)
			control.setAttribute(PRESSED, String(control.getAttribute(VALUE) === selected));

		active = selected;
		onFilterChange?.(selected);
		run(true);
	};

	filters.addEventListener(
		"click",
		(event) => {
			const target = event.target;

			if (!(target instanceof Element)) return;

			const value = target.closest<HTMLElement>(CONTROL)?.getAttribute(VALUE);

			if (value) press(value);
		},
		{ signal },
	);

	return {
		refresh: (animate = true) => run(animate),
		restore: () => {
			cancelHold?.();
			cancelHold = undefined;
			cancelAnimationFrame(frame);

			root.classList.remove(exitingClass, emptyClass);
			inertTarget.removeAttribute(INERT);

			for (const item of items) {
				item.classList.remove(enteringClass);
				item.style.removeProperty(indexProperty);
				item.toggleAttribute(HIDDEN, authored.hidden.get(item) === true);

				if (item.getAttribute("style") === "") item.removeAttribute("style");
			}

			for (const [control, value] of authored.pressed) {
				if (value === null) control.removeAttribute(PRESSED);
				else control.setAttribute(PRESSED, value);
			}
		},
	};
};
